import { formatCurrency } from './formatUtils';

/**
 * Helpers for report subtotals / grand totals / charts when amounts are in record currency
 * and should be aggregated in company base currency (same logic as list/detail views).
 */

export function parseDisplayConfigForReport(displayConfig) {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (e) {
      return {};
    }
  }
  return displayConfig || {};
}

export function reportFieldIsCurrencyLike(field) {
  if (!field) return false;
  const dt = String(field.data_type || '').toLowerCase();
  if (dt === 'currency' || dt === 'money') return true;
  if (dt === 'rollupsummary') {
    const parsed = parseDisplayConfigForReport(
      field.display_config || field.displayConfig
    );
    const outputType = String(
      parsed.rollupSummary?.outputType || 'number'
    ).toLowerCase();
    return outputType === 'currency';
  }
  return false;
}

export function unwrapReportScalar(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    const inner = val.value ?? val.displayValue;
    if (inner == null || inner === '') return null;
    if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
      return unwrapReportScalar(inner);
    }
    return inner;
  }
  return val;
}

/**
 * Read currency + exchange_rate for a report row for the given object (table alias prefix).
 */
export function getRowCurrencyAndRateFromReportRow(row, objectName) {
  if (!row) {
    return { currency: null, rate: NaN };
  }
  const table = objectName || 'main';
  const curKey = `${table}_currency`;
  const rateKey = `${table}_exchange_rate`;
  const c = row[curKey] ?? row.currency;
  const r = row[rateKey] ?? row.exchange_rate;
  return {
    currency: unwrapReportScalar(c),
    rate: Number(unwrapReportScalar(r)),
  };
}

/**
 * Build map: uppercase currency_code → exchange_rate from Currency API list
 * (same rates as currency_exchange_rate / record exchange_rate).
 */
export function buildExchangeRateLookupFromCurrencyList(list) {
  const lookup = Object.create(null);
  if (!Array.isArray(list)) return lookup;
  for (const row of list) {
    const code = row?.currency_code;
    if (code == null || String(code).trim() === '') continue;
    const rate = Number(row.exchange_rate);
    if (Number.isFinite(rate) && rate > 0) {
      lookup[String(code).trim().toUpperCase()] = rate;
    }
  }
  return lookup;
}

/**
 * Prefer row snapshot rate; if missing/invalid, use current org rate from lookup.
 */
export function resolveEffectiveExchangeRate(currencyCode, rowRate, lookup) {
  if (Number.isFinite(rowRate) && rowRate > 0) return rowRate;
  if (!lookup || currencyCode == null || String(currencyCode).trim() === '') {
    return NaN;
  }
  const key = String(currencyCode).trim().toUpperCase();
  const r = lookup[key];
  return Number.isFinite(r) && r > 0 ? r : NaN;
}

export function convertAmountToBase(amount, currency, rate, baseCode) {
  if (!Number.isFinite(amount)) return amount;
  if (!baseCode || !currency) return amount;
  if (
    String(currency).trim().toUpperCase() ===
    String(baseCode).trim().toUpperCase()
  ) {
    return amount;
  }
  if (!Number.isFinite(rate) || rate <= 0) return amount;
  return amount / rate;
}

export function formatAggregatedDisplayForReport(
  result,
  field,
  aggregation,
  baseCurrencyCode,
  formatValueImpl
) {
  if (aggregation === 'count') return String(result);
  if (reportFieldIsCurrencyLike(field) && baseCurrencyCode) {
    const decimalPlaces =
      field.scale !== undefined && field.scale !== null && field.scale !== ''
        ? parseInt(field.scale, 10)
        : 2;
    return formatCurrency(result, baseCurrencyCode, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }
  return formatValueImpl(result, field, aggregation);
}

function collectValuesForAggregation(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  exchangeRateLookup = null
) {
  if (!data || data.length === 0) return null;

  const tableName = field.object_name || 'main';
  const aliasedFieldName = `${tableName}_${field.field_name}`;
  const useBase =
    !!baseCurrencyCode && reportFieldIsCurrencyLike(field) && aggregation !== 'count';

  const values = data
    .map(row => {
      const fieldData = row[aliasedFieldName] || row[field.field_name];
      let num = null;
      if (typeof fieldData === 'object' && fieldData !== null) {
        const value = fieldData.value;
        if (value !== null && value !== undefined && value !== '') {
          num = parseFloat(value);
        }
      } else if (
        fieldData !== null &&
        fieldData !== undefined &&
        fieldData !== ''
      ) {
        num = parseFloat(fieldData);
      }
      if (num === null || isNaN(num)) return null;

      if (useBase) {
        const { currency, rate } = getRowCurrencyAndRateFromReportRow(
          row,
          tableName
        );
        const effectiveRate = resolveEffectiveExchangeRate(
          currency,
          rate,
          exchangeRateLookup
        );
        return convertAmountToBase(num, currency, effectiveRate, baseCurrencyCode);
      }
      return num;
    })
    .filter(val => val !== null && !isNaN(val));

  return values.length ? values : null;
}

/** Raw numeric aggregation (for charts). Returns null if N/A. Count returns integer. */
export function getAggregationNumericResult(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  exchangeRateLookup = null
) {
  const values = collectValuesForAggregation(
    data,
    field,
    aggregation,
    baseCurrencyCode,
    exchangeRateLookup
  );
  if (!values || values.length === 0) return null;

  switch (aggregation) {
    case 'sum':
      return values.reduce((sum, val) => sum + val, 0);
    case 'average':
    case 'avg':
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    }
    case 'count':
      return values.length;
    default:
      return null;
  }
}

export function calculateReportAggregation(
  data,
  field,
  aggregation,
  baseCurrencyCode,
  formatAggregatedDisplay,
  exchangeRateLookup = null
) {
  const values = collectValuesForAggregation(
    data,
    field,
    aggregation,
    baseCurrencyCode,
    exchangeRateLookup
  );
  if (!values || values.length === 0) return 'N/A';

  let result;
  switch (aggregation) {
    case 'sum':
      result = values.reduce((sum, val) => sum + val, 0);
      break;
    case 'average':
    case 'avg':
      result = values.reduce((sum, val) => sum + val, 0) / values.length;
      break;
    case 'max':
      result = Math.max(...values);
      break;
    case 'min':
      result = Math.min(...values);
      break;
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      result =
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
      break;
    }
    case 'count':
      return values.length.toString();
    default:
      return 'N/A';
  }

  return formatAggregatedDisplay(result, field, aggregation);
}

/**
 * Single numeric for export subtotals (0 when unparsable; converts to base when applicable).
 */
export function getAggregatableNumericForExport(
  field,
  row,
  baseCurrencyCode,
  getFieldValueForExport,
  exchangeRateLookup = null
) {
  const v = parseFloat(getFieldValueForExport(field, row));
  const n = isNaN(v) ? 0 : v;
  if (!baseCurrencyCode || !reportFieldIsCurrencyLike(field)) return n;
  const { currency, rate } = getRowCurrencyAndRateFromReportRow(
    row,
    field.object_name || 'main'
  );
  const effectiveRate = resolveEffectiveExchangeRate(
    currency,
    rate,
    exchangeRateLookup
  );
  return convertAmountToBase(n, currency, effectiveRate, baseCurrencyCode);
}

function extractNumericFromReportFieldData(fieldData) {
  if (fieldData == null) return null;
  const raw =
    typeof fieldData === 'object' && fieldData !== null
      ? unwrapReportScalar(fieldData) ?? fieldData.value
      : fieldData;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Currency / rollup-currency report cells: format in record currency (correct symbol)
 * and append base equivalent when record currency ≠ base and exchange_rate is valid.
 * @returns {string|null} null → caller should use default formatValue
 * @param {Record<string, number>|null} exchangeRateLookup — from buildExchangeRateLookupFromCurrencyList; used when row has no valid exchange_rate
 */
export function formatReportCurrencyCell(
  fieldData,
  field,
  row,
  baseCurrencyCode,
  fallbackCurrencyCode = 'INR',
  exchangeRateLookup = null
) {
  if (!field || !row) return null;
  if (!reportFieldIsCurrencyLike(field)) return null;

  const num = extractNumericFromReportFieldData(fieldData);
  if (num == null) return null;

  const tableName = field.object_name || 'main';
  const { currency: recordCurrencyRaw, rate: rowRate } =
    getRowCurrencyAndRateFromReportRow(row, tableName);
  const recordCurrency =
    recordCurrencyRaw && String(recordCurrencyRaw).trim()
      ? String(recordCurrencyRaw).trim().toUpperCase()
      : null;

  const decimalPlaces =
    field.scale !== undefined && field.scale !== null && field.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  const opts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  const codeForFormat = recordCurrency || fallbackCurrencyCode;
  let out = formatCurrency(num, codeForFormat, opts);

  const base = baseCurrencyCode
    ? String(baseCurrencyCode).trim().toUpperCase()
    : null;
  const effectiveRate = resolveEffectiveExchangeRate(
    recordCurrency,
    rowRate,
    exchangeRateLookup
  );
  if (
    base &&
    recordCurrency &&
    recordCurrency !== base &&
    Number.isFinite(effectiveRate) &&
    effectiveRate > 0
  ) {
    const baseEquiv = num / effectiveRate;
    out += ` (${formatCurrency(baseEquiv, base, opts)} ${base})`;
  }

  return out;
}
